import axios, { AxiosError, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { storage } from '@/constants/storage';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';

// Token refresh threshold — refresh when less than 5 minutes remain
const REFRESH_THRESHOLD_MS = 5 * 60 * 1000;

// Create axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds default; override per-request for long operations
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Decode JWT payload without verification (browser-side exp check only).
 * Returns null if token is malformed.
 */
function decodeTokenExp(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

/**
 * Check if a token is within the refresh threshold (close to expiry but not yet expired).
 * Returns false for already-expired tokens (no point refreshing those).
 */
function isTokenExpiringSoon(token: string): boolean {
  const exp = decodeTokenExp(token);
  if (exp === null) return false;
  const remainingMs = exp * 1000 - Date.now();
  return remainingMs > 0 && remainingMs < REFRESH_THRESHOLD_MS;
}

// Mutex for token refresh — prevents concurrent refresh calls
let refreshPromise: Promise<string | null> | null = null;

/**
 * Refresh the access token. Only one refresh runs at a time;
 * concurrent callers share the same promise.
 */
async function refreshAccessToken(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const response = await axios.post<{ access_token: string }>(
        `${API_BASE_URL}/auth/refresh`,
        null,
        { headers: { Authorization: `Bearer ${storage.getAccessToken()}` } }
      );
      const newToken = response.data.access_token;
      storage.setAccessToken(newToken);
      return newToken;
    } catch {
      // Refresh failed — token may already be expired
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

// Request interceptor - add auth token + proactive refresh
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const token = storage.getAccessToken();
    if (token) {
      // Proactively refresh if token is close to expiry
      if (isTokenExpiringSoon(token)) {
        const newToken = await refreshAccessToken();
        config.headers.Authorization = `Bearer ${newToken || token}`;
      } else {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;
      const url = error.config?.url;

      // Log ALL API errors with full response body for debugging
      console.error(`[API Error] ${status} ${error.config?.method?.toUpperCase()} ${url}`, {
        status,
        url,
        method: error.config?.method,
        responseBody: data,
        requestData: error.config?.data,
      });

      if (status === 401) {
        // Unauthorized - only clear auth and redirect if we're on a protected page
        // Don't clear auth if we're already on the login page (might be optional requests)
        const currentPath = window.location.pathname;
        const isLoginPage = currentPath === '/login';

        console.error('[API] 401 Unauthorized:', {
          url,
          currentPath,
          isLoginPage,
          hasToken: !!storage.getAccessToken(),
        });

        if (!isLoginPage) {
          // User was on a protected page but token is invalid/expired
          storage.clearAuth();
          window.location.href = '/login';
          console.warn('[API] Authentication failed - redirecting to login');
        }
        // On login page - expected 401 for unauthenticated requests
      }
    } else if (error.request) {
      // Request made but no response
      console.error('[API Error] No response from server:', error.request);
    } else {
      // Error setting up request
      console.error('[API Error] Request setup failed:', error.message);
    }
    return Promise.reject(error);
  }
);

// Generic API request function
export async function apiRequest<T>(
  config: AxiosRequestConfig
): Promise<T> {
  const response = await apiClient.request<T>(config);
  return response.data;
}

// Export axios instance for custom usage
export default apiClient;
